import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  SyutSearchGrid,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useMealPlans, useDeleteMealPlan } from 'hooks/api/useMealPlan';
import { propertyService } from 'api/services/propertyService';
import type { MealPlan, MealPlanSearchRequest, Property } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import { toMainMeal } from 'utils/mealPlanMeals';
import MealPlanModal from 'components/modals/modules/meal-plan-module/MealPlanModal';
import MealPlanViewModal from 'components/modals/modules/meal-plan-module/MealPlanViewModal';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ViewMode = 'list' | 'grid';

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

// ============================================================================
// DROPDOWN OPTIONS
// ============================================================================

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select', label: '', field: 'select', required: true, defaultVisible: true, width: 50, sortable: false },
  { id: 'planCode', label: 'Plan Code', field: 'planCode', required: false, defaultVisible: true, sortable: true },
  { id: 'planName', label: 'Plan Name', field: 'planName', required: false, defaultVisible: true, sortable: true },
  { id: 'property', label: 'Property', field: 'propertyId', required: false, defaultVisible: true, sortable: false },
  { id: 'includesBreakfast', label: 'Breakfast', field: 'includesBreakfast', required: false, defaultVisible: true, sortable: true },
  { id: 'mainMeal', label: 'Main Meal', field: 'mainMeal', required: false, defaultVisible: true, sortable: false },
  { id: 'adultRate', label: 'Adult Rate', field: 'adultRate', required: false, defaultVisible: true, sortable: true },
  { id: 'childRate', label: 'Child Rate', field: 'childRate', required: false, defaultVisible: true, sortable: true },
  { id: 'isActive', label: 'Status', field: 'isActive', required: false, defaultVisible: true, sortable: true },
  { id: 'description', label: 'Description', field: 'description', required: false, defaultVisible: false, sortable: false },
  { id: 'actions', label: 'Actions', field: 'actions', required: true, defaultVisible: true, width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 0.5rem)',
          right: 0,
          width: '320px',
          maxHeight: '500px',
          background: 'var(--bg-primary)',
          border: '2px solid var(--primary)',
          borderRadius: '0.75rem',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          animation: 'slideDown 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '2px solid var(--border-color)',
            background: 'var(--bg-secondary)',
            borderRadius: '0.75rem 0.75rem 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Manage Columns
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
            {columns.map(column => (
              <div
                key={column.id}
                onClick={() => toggleColumn(column.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.625rem 1.25rem',
                  cursor: column.required ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  gap: '0.75rem',
                  opacity: column.required ? 0.6 : 1,
                }}
              >
                <div style={{
                  width: '1.125rem',
                  height: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--border-color)',
                  borderRadius: '0.25rem',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  ...(selectedColumns.includes(column.id) ? { background: 'var(--accent-gradient)', borderColor: 'var(--primary)', color: 'white' } : {}),
                }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                </div>
                <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                  {column.label}{column.required && ' *'}
                </span>
              </div>
            ))}
          </div>

          <div style={{
            padding: '1rem',
            borderTop: '2px solid var(--border-color)',
            background: 'var(--bg-primary)',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleSelectAll}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}
              >
                {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={() => setConfirmResetOpen(true)}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}
              >
                Reset Default
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setSelectedColumns(initialVisible); setIsOpen(false); }}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={() => { onApply(selectedColumns); setIsOpen(false); }}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// MEAL PLAN CARD COMPONENT (Grid View)
// ============================================================================

interface MealPlanCardProps {
  mealPlan: MealPlan;
  onView: (mp: MealPlan) => void;
  onEdit: (mp: MealPlan) => void;
  onCopy: (mp: MealPlan) => void;
  onDelete: (mp: MealPlan) => void;
}

const MealPlanCard: React.FC<MealPlanCardProps> = ({ mealPlan, onView, onEdit, onCopy, onDelete }) => {
  const menuItems: ActionMenuItem[] = [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => onView(mealPlan) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => onEdit(mealPlan) },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => onCopy(mealPlan) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => onDelete(mealPlan), variant: 'danger' },
  ];

  const meals = [
    mealPlan.includesBreakfast && 'B',
    ...{ NONE: [], LUNCH: ['L'], DINNER: ['D'], BOTH: ['L', 'D'], ONE_OF: ['L/D'] }[toMainMeal(mealPlan)],
  ].filter(Boolean);

  return (
    <div
      style={{
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.75rem',
        padding: '1.25rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s ease',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            {mealPlan.planCode}
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.5rem' }}>
            {mealPlan.planName}
          </h3>
        </div>
        <ActionMenu items={menuItems} />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--border-color)',
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Meals</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>
            {meals.length > 0 ? meals.join(', ') : 'None'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Adult Rate</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>
            {mealPlan.adultRate != null ? mealPlan.adultRate.toFixed(2) : '-'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Child Rate</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>
            {mealPlan.childRate != null ? mealPlan.childRate.toFixed(2) : '-'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          <span className={`badge badge-${mealPlan.isActive ? 'active' : 'inactive'}`}>
            {mealPlan.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface MealPlanMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const MealPlanMasterScreen: React.FC<MealPlanMasterScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --- Property lookup for display names ---
  const [propertyLookup, setPropertyLookup] = useState<Record<string, Property>>({});

  // --- Column visibility ---
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    return columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
  });

  // --- Server-side pagination & search state ---
  const [queryParams, setQueryParams] = useState<MealPlanSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, pagination, refetch } = useMealPlans(queryParams);
  const { deleteMealPlan, loading: isDeleting } = useDeleteMealPlan();

  // --- View state ---
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // --- Filter state ---
  const [filterProperty, setFilterProperty] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // --- Modal state ---
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<MealPlan | null>(null);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // --- Load property lookup (for grid display names) ---
  const loadLookups = useCallback(async () => {
    try {
      const propRes = await propertyService.getAll({ pageSize: 500 });
      const lookup: Record<string, Property> = {};
      propRes.data.forEach(p => { lookup[p.id] = p; });
      setPropertyLookup(lookup);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadLookups(); }, [loadLookups]);

  // --- Async property options for filter panel (always fetches latest from DB) ---
  const loadPropertyFilterOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: inputValue || undefined, pageSize: 50, includeInactive: false });
    return (res.data || []).map(p => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  // --- Search debounce (server-side, 400ms) ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --- Server-side pagination handlers ---
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // --- Column selector handlers ---
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('meal-plan-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('meal-plan-master-columns');
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('meal-plan-master-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  // --- F2 keyboard shortcut for search popup ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !deleteModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, deleteModalOpen]);

  // --- View handler ---
  const handleView = useCallback((mp: MealPlan) => {
    const viewId = tabId ? `${tabId}-view-modal-${mp.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(mp);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((id: string) => {
    loadLookups();
    const mp = data.find(r => r.id === id);
    if (mp) {
      setSelectedRecord(mp);
      setViewModalOpen(true);
    }
  }, [data, loadLookups]);

  // --- Edit handler ---
  const handleEdit = useCallback((mp: MealPlan) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${mp.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(mp);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  // --- Copy handler (multi-instance) ---
  const handleCopy = useCallback((mp: MealPlan) => {
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++copyInstanceCounter.current);
    setCopyInstanceId(newId);
    setSelectedRecord(mp);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  // --- Add New handler ---
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  // --- Delete handler ---
  const handleDeleteClick = useCallback((mp: MealPlan) => {
    setSelectedRecord(mp);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await deleteMealPlan(selectedRecord.id as string, selectedRecord.rowVersion);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedRecord.planName}" has been deleted successfully.`);
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while deleting. Please try again.');
    }
  }, [selectedRecord, deleteMealPlan, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
    loadLookups();
  }, [refetch, loadLookups]);

  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, planCode?: string) => {
    loadLookups();
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (planCode) {
      const mp = data.find(r => r.planCode === planCode);
      if (mp) setSelectedRecord(mp);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, [data, loadLookups]);

  // --- Column definitions for SyutGrid ---
  const tableColumns: SyutGridColumn<MealPlan>[] = useMemo(() => [
    { id: 'planCode', header: 'Plan Code', accessorKey: 'planCode', size: 120, enableSorting: true },
    { id: 'planName', header: 'Plan Name', accessorKey: 'planName', size: 200, enableSorting: true },
    {
      id: 'property',
      header: 'Property',
      accessorKey: 'propertyId',
      size: 200,
      enableSorting: false,
      cell: ({ getValue }) => {
        const pid = getValue() as string | undefined;
        if (!pid) return <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>—</span>;
        const prop = propertyLookup[pid];
        return <span style={{ fontSize: '0.85rem' }}>{prop ? `${prop.propertyCode} - ${prop.propertyName}` : pid}</span>;
      },
    },
    {
      id: 'includesBreakfast',
      header: 'Breakfast',
      accessorKey: 'includesBreakfast',
      size: 100,
      cell: ({ getValue }) => {
        const val = getValue() as boolean;
        return <span style={{ fontSize: '0.85rem', color: val ? 'var(--text-primary)' : 'var(--text-muted)' }}>{val ? 'Yes' : 'No'}</span>;
      },
    },
    {
      id: 'mainMeal',
      header: 'Main Meal',
      size: 190,
      enableSorting: false,
      cell: ({ row }) => {
        const m = row.original;
        const label = {
          NONE: 'None',
          LUNCH: 'Lunch',
          DINNER: 'Dinner',
          BOTH: 'Lunch + Dinner',
          ONE_OF: `Lunch or Dinner (default ${m.defaultMajorMeal === 'LUNCH' ? 'Lunch' : 'Dinner'})`,
        }[toMainMeal(m)];
        return <span style={{ fontSize: '0.85rem', color: label === 'None' ? 'var(--text-muted)' : 'var(--text-primary)' }}>{label}</span>;
      },
    },
    {
      id: 'adultRate',
      header: 'Adult Rate',
      accessorKey: 'adultRate',
      size: 120,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as number | undefined;
        return <span style={{ fontSize: '0.85rem' }}>{val != null ? val.toFixed(2) : '-'}</span>;
      },
    },
    {
      id: 'childRate',
      header: 'Child Rate',
      accessorKey: 'childRate',
      size: 120,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as number | undefined;
        return <span style={{ fontSize: '0.85rem' }}>{val != null ? val.toFixed(2) : '-'}</span>;
      },
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 100,
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return (
          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    { id: 'description', header: 'Description', accessorKey: 'description', size: 250, enableSorting: false },
  ], [propertyLookup]);

  // --- Row actions ---
  const getRowActions = useCallback((mp: MealPlan): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      items.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(mp) });
    }
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(mp) });
    }
    if (allowedActions.includes('add')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(mp) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(mp), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleView, handleEdit, handleCopy, handleDeleteClick]);

  // --- View mode options ---
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      {
        id: 'search',
        icon: <IconSystem.search size={18} />,
        label: 'Search Meal Plans',
        onClick: () => setSearchPopupOpen(true),
        tooltip: 'Search Meal Plans (F2)',
        variant: 'icon',
      },
    ];
    if (allowedActions.includes('add')) {
      buttons.push({
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add Meal Plan',
        onClick: handleAddNew,
        tooltip: 'Add New Meal Plan',
        variant: 'icon',
      });
    }
    buttons.push({
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    });
    if (allowedActions.includes('export')) {
      buttons.push({
        id: 'export',
        icon: <IconSystem.download size={18} />,
        label: 'Export',
        onClick: () => console.log('Export clicked'),
        tooltip: 'Export',
        variant: 'icon',
      });
    }
    return buttons;
  }, [allowedActions, refetch, handleAddNew]);

  // --- Filter Apply ---
  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      propertyId: filterProperty || undefined,
      includeInactive: filterStatus === 'true' ? false : true,
      page: 1,
    }));
  }, [filterProperty, filterStatus]);

  // --- Filter Clear ---
  const handleFilterClear = useCallback(() => {
    setFilterProperty('');
    setFilterStatus('');
    setQueryParams(prev => ({
      ...prev,
      propertyId: undefined,
      includeInactive: undefined,
      page: 1,
    }));
  }, []);

  // --- RENDER ---
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Meal Plan Master"
        description="Manage meal plans and their configurations"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search meal plans...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
          appliedCount: [filterProperty, filterStatus].filter(Boolean).length,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      {/* REQUIRED: marginTop: '6px' — the ONLY allowed inline style on .module-content */}
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={data}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage="No meal plans found"
              frozenLeftColumns={['select', 'planCode']}
              frozenRightColumns={['actions']}
              visibleColumns={visibleColumns}
            />
          )}

          {viewMode === 'grid' && (() => {
            const currentPage = queryParams.page ?? 1;
            const currentPageSize = queryParams.pageSize ?? 25;
            const totalPages = Math.max(1, Math.ceil(pagination.totalItems / currentPageSize));
            return (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem',
                  flex: 1,
                  overflow: 'auto',
                  alignContent: 'start',
                }}>
                  {data.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.tag size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>No meal plans found</p>
                    </div>
                  ) : (
                    data.map((mp) => (
                      <MealPlanCard
                        key={mp.id as string}
                        mealPlan={mp}
                        onView={handleView}
                        onEdit={handleEdit}
                        onCopy={handleCopy}
                        onDelete={handleDeleteClick}
                      />
                    ))
                  )}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={currentPageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage - 2)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* REQUIRED: Filter panel as SIBLING of data-grid-container */}
        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => setFilterOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text-muted)',
                borderRadius: '0.25rem',
              }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Property"
                isAsync
                loadOptions={loadPropertyFilterOptions}
                defaultOptions
                cacheOptions={false}
                value={filterProperty}
                onChange={(val: string | string[] | null) => setFilterProperty((val as string) || '')}
                placeholder="Select property..."
                isClearable
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={statusOptions}
                value={filterStatus}
                onChange={(val: string | string[] | null) => setFilterStatus((val as string) || '')}
                placeholder="Select status..."
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleFilterClear}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleFilterApply}>
              Apply
            </button>
          </div>
        </div>}
      </div>

      {/* View Modal */}
      <MealPlanViewModal
        isOpen={viewModalOpen}
        mealPlan={selectedRecord}
        propertyLookup={propertyLookup}
        onClose={() => setViewModalOpen(false)}
        onEdit={(mp) => handleEdit(mp)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* Form Modal */}
      <MealPlanModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedRecord?.planName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      {/* Meal Plan Search Popup (F2) */}
      <SyutSearchGrid<MealPlan>
        isOpen={searchPopupOpen}
        onClose={() => setSearchPopupOpen(false)}
        onSelect={(mp) => handleView(mp)}
        title="Search Meal Plans"
        columns={[
          { key: 'planCode', header: 'Code', width: '100px', sortable: true },
          { key: 'planName', header: 'Plan Name', width: '200px', sortable: true },
          {
            key: 'adultRate',
            header: 'Adult Rate',
            width: '120px',
            sortable: true,
            render: (value: any) => value != null ? Number(value).toFixed(2) : '-',
          },
          {
            key: 'isActive',
            header: 'Status',
            width: '100px',
            render: (value: any) => (
              <span style={{
                padding: '0.125rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500,
                background: value ? 'var(--badge-active-bg, #dcfce7)' : 'var(--badge-inactive-bg, #fee2e2)',
                color: value ? 'var(--badge-active-color, #166534)' : 'var(--badge-inactive-color, #991b1b)',
              }}>
                {value ? 'Active' : 'Inactive'}
              </span>
            ),
          },
        ]}
        data={data}
        filters={[
          {
            key: 'isActive',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ],
          },
        ]}
        rowKey="id"
        enableRecentlyUsed
        recentlyUsedKey="meal-plan-master-search"
        searchPlaceholder="Search by code or name... (F2 to focus)"
      />
    </div>
  );
};

export default MealPlanMasterScreen;
